import { useState } from "react";
import { Link } from "react-router";
import { requestPasswordReset, type PasswordResetResult } from "@/lib/api";
import { AuthLogo } from "./AuthLogo";

export function ForgotPasswordPage() {
	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<PasswordResetResult | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
		e.preventDefault();
		setLoading(true);
		setError(null);
		try {
			// The "submitted" result is returned regardless of whether the email
			// matched an account — the backend deliberately doesn't tell us.
			setResult(await requestPasswordReset(email));
		} catch {
			setError("通信エラーが発生しました。時間をおいて再度お試しください。");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div
			style={{
				minHeight: "100vh",
				background: "var(--surface-2)",
				display: "grid",
				placeItems: "center",
				fontFamily: "var(--font-sans)",
			}}
		>
			<div
				style={{
					width: "100%",
					maxWidth: 360,
					background: "var(--surface-1)",
					borderRadius: "var(--radius-5)",
					boxShadow: "var(--shadow-card)",
					padding: "32px",
					display: "flex",
					flexDirection: "column",
					gap: 24,
				}}
			>
				<header style={{ display: "flex", flexDirection: "column", gap: 4 }}>
					<AuthLogo />
					<h1
						style={{
							margin: 0,
							fontSize: "var(--text-display)",
							fontWeight: 600,
							lineHeight: "var(--leading-snug)",
							letterSpacing: "var(--tracking-display)",
							color: "var(--fg-1)",
						}}
					>
						パスワードの再設定
					</h1>
					<p
						style={{
							margin: 0,
							fontSize: "var(--text-ui)",
							color: "var(--fg-2)",
							lineHeight: "var(--leading-ui)",
						}}
					>
						登録済みのメールアドレスを入力してください
					</p>
				</header>

				{result !== null ? (
					<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
						{result === "rateLimited" ? (
							<div
								style={{
									padding: "10px 12px",
									borderRadius: "var(--radius-3)",
									background: "var(--warning-subtle)",
									boxShadow: "0 0 0 1px var(--warning-line)",
									fontSize: "var(--text-caption)",
									color: "var(--warning)",
									lineHeight: "var(--leading-ui)",
								}}
							>
								短時間にリクエストが多すぎます。しばらく時間をおいてから再度お試しください。
							</div>
						) : (
							<div
								style={{
									padding: "10px 12px",
									borderRadius: "var(--radius-3)",
									background: "var(--success-subtle)",
									boxShadow: "0 0 0 1px var(--success-line)",
									fontSize: "var(--text-caption)",
									color: "var(--success)",
									lineHeight: "var(--leading-ui)",
								}}
							>
								ご入力のメールアドレスに該当するアカウントが存在する場合、招待リンクをお送りしました。メールをご確認ください。
							</div>
						)}
						<Link
							to="/login"
							style={{
								fontSize: "var(--text-caption)",
								color: "var(--accent)",
								textDecoration: "none",
								alignSelf: "flex-start",
							}}
						>
							サインインに戻る
						</Link>
					</div>
				) : (
					<form
						onSubmit={handleSubmit}
						style={{ display: "flex", flexDirection: "column", gap: 12 }}
					>
						<label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{
									fontSize: "var(--text-caption)",
									fontWeight: 500,
									color: "var(--fg-2)",
									letterSpacing: "var(--tracking-wide)",
								}}
							>
								メールアドレス
							</span>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="you@company.com"
								required
								autoComplete="email"
								style={{
									height: 36,
									padding: "0 10px",
									fontSize: "var(--text-ui)",
									color: "var(--fg-1)",
									background: "var(--surface-1)",
									border: "none",
									borderRadius: "var(--radius-3)",
									boxShadow: "var(--shadow-ring)",
									outline: "none",
									fontFamily: "var(--font-sans)",
									transition: "box-shadow 150ms ease",
								}}
								onFocus={(e) => {
									e.currentTarget.style.boxShadow = "var(--shadow-focus)";
								}}
								onBlur={(e) => {
									e.currentTarget.style.boxShadow = "var(--shadow-ring)";
								}}
							/>
						</label>

						{error && (
							<div
								style={{
									padding: "8px 10px",
									borderRadius: "var(--radius-3)",
									background: "var(--danger-subtle)",
									boxShadow: "0 0 0 1px var(--danger-line)",
									fontSize: "var(--text-caption)",
									color: "var(--danger)",
									lineHeight: "var(--leading-ui)",
								}}
							>
								{error}
							</div>
						)}

						<button
							type="submit"
							disabled={loading}
							style={{
								marginTop: 4,
								height: 36,
								padding: "0 16px",
								fontSize: "var(--text-ui)",
								fontWeight: 500,
								fontFamily: "var(--font-sans)",
								color: loading ? "var(--on-neutral-solid)" : "var(--on-accent-solid)",
								background: loading ? "var(--neutral-solid)" : "var(--accent-solid)",
								border: "none",
								borderRadius: "var(--radius-3)",
								cursor: loading ? "not-allowed" : "pointer",
								transition: "background 150ms ease, opacity 150ms ease",
								letterSpacing: "var(--tracking-tight)",
							}}
							onMouseEnter={(e) => {
								if (!loading) e.currentTarget.style.background = "var(--accent-solid-hover)";
							}}
							onMouseLeave={(e) => {
								if (!loading) e.currentTarget.style.background = "var(--accent-solid)";
							}}
						>
							{loading ? "送信中…" : "招待リンクを送信"}
						</button>

						<Link
							to="/login"
							style={{
								marginTop: 4,
								fontSize: "var(--text-caption)",
								color: "var(--accent)",
								textDecoration: "none",
								alignSelf: "flex-start",
							}}
						>
							サインインに戻る
						</Link>
					</form>
				)}
			</div>
		</div>
	);
}
